import { Form, Input } from "antd"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import ButtonComponent from "../Button"
import PasswordValidator from "../PasswordValidator"

type FormErrorText = string | string[]

interface NewPasswordFormValues {
  password: string
  confirm: string
}

interface NewPasswordFormProps {
  error?: string | Record<string, FormErrorText> | null
  onSubmit: (password: string) => void
}

function NewPasswordForm({ error, ...props }: NewPasswordFormProps) {
  const [value, setValue] = useState<string | null>(null)
  const [valueConfirm, setValueConfirm] = useState<string | null>(null)
  const [errorText, setErrorText] = useState<FormErrorText | null>(null)

  const [form] = Form.useForm()
  const { t } = useTranslation()

  useEffect(() => {
    if (error) {
      if (typeof error === "string") {
        if (error.length > 100) {
          setErrorText(t("Something went wrong, please try later again"))
        } else {
          setErrorText(error)
        }
      } else {
        setErrorText(Object.values(error)[0])
      }
    }
  }, [error])

  const onFinish = (values: NewPasswordFormValues) => {
    const data = { ...values }
    props.onSubmit(data.password)
  }

  const onFinishFailed = ({
    errorFields
  }: {
    errorFields?: Array<{ errors: string[] }>
  }) => {
    if (errorFields) setErrorText(errorFields[0]["errors"])
  }

  function handleClearError(
    e: React.FocusEvent<HTMLInputElement>,
    field: string
  ) {
    form.setFields([
      {
        name: field,
        errors: []
      }
    ])
    setErrorText(null)
  }

  function onFocusHandler(e: React.FocusEvent<HTMLInputElement>) {
    const target = e.target
    handleClearError(e, target.name)
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { name, value } = e.target
    if (name === "password") setValue(value)
    if (name === "confirm") setValueConfirm(value)
  }

  return (
    <Form
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      validateTrigger={["onSubmit", "onChange"]}
      className="login-form"
      layout="vertical"
      form={form}
      //requiredMark={false}
    >
      <Form.Item
        label={t("New Password")}
        name="password"
        validateStatus={errorText ? "error" : undefined}
        rules={[{ required: true, message: "Required field" }]}
        style={{ marginBottom: 8 }}
      >
        <Input.Password
          size="large"
          name="password"
          placeholder={`${t("New Password")}...`}
          autoComplete="dontshow"
          visibilityToggle
          onChange={(e) => handleChange(e)}
          onFocus={(e) => onFocusHandler(e)}
        />
      </Form.Item>

      <PasswordValidator value={value} error={errorText} isLogin={true} />

      <Form.Item
        label={t("Confirm new password")}
        name="confirm"
        style={{ marginBottom: 0 }}
        rules={[
          {
            required: true,
            message: "Please confirm your password!"
          },
          ({ getFieldValue }) => ({
            validator(rule, value) {
              if (
                !value ||
                !getFieldValue("password") ||
                getFieldValue("password") === value
              ) {
                if (value?.length < 8) {
                  return Promise.reject("Must be at least 8 characters")
                } else {
                  return Promise.resolve()
                }
              } else {
                return Promise.reject("Passwords do not match")
              }
            }
          })
        ]}
      >
        <Input.Password
          size="large"
          name="confirm"
          placeholder={`${t("Confirm new password")}...`}
          autoComplete="dontshow"
          visibilityToggle
          onChange={(e) => handleChange(e)}
          onFocus={(e) => onFocusHandler(e)}
        />
      </Form.Item>

      <ButtonComponent
        size="large"
        type="primary"
        htmlType="submit"
        block
        disabled={
          !valueConfirm ||
          (value?.length as number) < 6 ||
          valueConfirm?.length < 6
        }
      >
        {t("Reset password")}
      </ButtonComponent>
    </Form>
  )
}

export default NewPasswordForm
